import React, { useEffect, useState } from 'react';
import { X, Loader2, Save, Trash2, Plus, Zap, MessageSquare, Clock, MousePointer2, Tag, Check, AlertCircle } from 'lucide-react';
import { API_URL } from '../lib/apiConfig';

const safeToken = () => { try { return localStorage.getItem('autopostr_token'); } catch { return null; } };

const readResponse = async (r) => {
  const ct = r.headers.get('content-type') || '';
  const text = await r.text();
  if (!ct.includes('json')) return { parseError: true, text: text.slice(0, 200) };
  try { return JSON.parse(text); } catch { return { parseError: true, text: text.slice(0, 200) }; }
};

const formatError = (body, fallback = 'Request failed') => {
  if (!body) return fallback;
  if (body.parseError) return `Server returned a non-JSON response (${body.text ? body.text.slice(0, 80) + '…' : 'empty'}).`;
  const d = body.detail;
  if (typeof d === 'string') return d;
  if (d && typeof d === 'object') {
    const msg = d.message || d.code || fallback;
    const reasons = d.reasons || [];
    return reasons.length > 1 ? `${msg}\n• ${reasons.join('\n• ')}` : msg;
  }
  return body.message || fallback;
};

const TRIGGER_TYPES = [
  { v: 'DM_KEYWORD', label: 'DM keyword' },
  { v: 'STORY_REPLY', label: 'Story reply' },
  { v: 'COMMENT_KEYWORD', label: 'Comment keyword' },
  { v: 'DM_WELCOME', label: 'First DM (welcome)' },
];

const NODE_META = {
  trigger: { icon: <Zap size={14} />, label: 'Trigger', color: 'bg-purple-500/15 text-purple-400 border-purple-500/30' },
  sendMessage: { icon: <MessageSquare size={14} />, label: 'Send message', color: 'bg-blue-500/15 text-blue-400 border-blue-500/30' },
  sendButtons: { icon: <MousePointer2 size={14} />, label: 'Message + buttons', color: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' },
  delay: { icon: <Clock size={14} />, label: 'Delay', color: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30' },
  tagUser: { icon: <Tag size={14} />, label: 'Tag user', color: 'bg-pink-500/15 text-pink-400 border-pink-500/30' },
};

const nextId = (nodes) => {
  const nums = nodes.map(n => parseInt(n.id, 10)).filter(n => !isNaN(n));
  return String((nums.length ? Math.max(...nums) : 0) + 1);
};

export default function FlowEditor({ flowId, onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [flow, setFlow] = useState(null);
  const [nodes, setNodes] = useState([]);
  const [name, setName] = useState('');
  const [status, setStatus] = useState('DRAFT');
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/dm/flows/${flowId}`, { headers: { Authorization: `Bearer ${safeToken()}` } });
        const d = await readResponse(r);
        if (!r.ok) throw new Error(formatError(d, 'Failed to load flow'));
        setFlow(d);
        setNodes(d.nodes || []);
        setName(d.name || '');
        setStatus(d.status || 'DRAFT');
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [flowId]);

  const updateNode = (id, patch) => {
    setNodes(prev => prev.map(n => n.id === id ? { ...n, data: { ...n.data, ...patch } } : n));
  };

  const removeNode = (id) => {
    setNodes(prev => prev.filter(n => n.id !== id));
  };

  const addNode = (type) => {
    const id = nextId(nodes);
    const base = { id, type, position: { x: 250, y: 150 * nodes.length }, data: { label: NODE_META[type].label } };
    if (type === 'sendMessage') base.data.message = 'Your message here';
    if (type === 'sendButtons') { base.data.message = 'Pick one:'; base.data.buttons = [{ type: 'web_url', title: 'Open', url: 'https://' }]; }
    if (type === 'delay') base.data.duration = 5;
    if (type === 'tagUser') base.data.tagName = 'Engaged';
    if (type === 'trigger') { base.data.triggerType = 'DM_KEYWORD'; base.data.keyword = 'HELLO'; }
    setNodes(prev => [...prev, base]);
  };

  const updateButton = (nodeId, idx, patch) => {
    setNodes(prev => prev.map(n => {
      if (n.id !== nodeId) return n;
      const buttons = [...(n.data.buttons || [])];
      buttons[idx] = { ...buttons[idx], ...patch };
      return { ...n, data: { ...n.data, buttons } };
    }));
  };

  const addButton = (nodeId) => {
    setNodes(prev => prev.map(n => n.id === nodeId
      ? { ...n, data: { ...n.data, buttons: [...(n.data.buttons || []), { type: 'web_url', title: 'Open', url: 'https://' }] } }
      : n));
  };

  const removeButton = (nodeId, idx) => {
    setNodes(prev => prev.map(n => n.id === nodeId
      ? { ...n, data: { ...n.data, buttons: (n.data.buttons || []).filter((_, i) => i !== idx) } }
      : n));
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      // Keep existing edges intact; a full node delete may leave orphan edges — recompute.
      let edges = flow?.edges || [];
      const nodeIds = new Set(nodes.map(n => n.id));
      edges = edges.filter(e => nodeIds.has(e.source) && nodeIds.has(e.target));
      const r = await fetch(`${API_URL}/api/dm/flows/${flowId}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${safeToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, status, nodes, edges })
      });
      const d = await readResponse(r);
      if (!r.ok) throw new Error(formatError(d, 'Save failed'));
      if (onSaved) onSaved(d.flow || {});
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="flow-editor-modal">
      <div className="bg-[#0f0f0f] border border-white/10 rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-5 border-b border-white/10 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <input data-testid="flow-editor-name" value={name} onChange={e => setName(e.target.value)}
              className="w-full bg-transparent text-xl font-semibold text-white outline-none" />
            <p className="text-white/40 text-xs mt-1">Flow {flowId}</p>
          </div>
          <select data-testid="flow-editor-status" value={status} onChange={e => setStatus(e.target.value)}
            className="px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-white text-sm">
            <option value="DRAFT" className="bg-[#1a1a1a]">DRAFT</option>
            <option value="ACTIVE" className="bg-[#1a1a1a]">ACTIVE</option>
            <option value="PAUSED" className="bg-[#1a1a1a]">PAUSED</option>
          </select>
          <button onClick={onClose} className="text-white/60 hover:text-white"><X size={20} /></button>
        </div>

        {loading ? (
          <div className="p-12 text-center text-white/50"><Loader2 className="animate-spin inline mr-2" />Loading flow…</div>
        ) : error ? (
          <div className="p-6 text-center text-red-400 flex items-center justify-center gap-2"><AlertCircle size={16} />{error}</div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto p-5 space-y-3" data-testid="flow-editor-nodes">
              {nodes.map((n, idx) => {
                const meta = NODE_META[n.type] || NODE_META.sendMessage;
                return (
                  <div key={n.id} data-testid={`flow-node-${n.id}`} className="rounded-xl bg-[#161616] border border-white/10 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-white/40 text-xs">#{idx + 1}</span>
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs border ${meta.color}`}>{meta.icon}{meta.label}</span>
                        {n.type !== 'trigger' && (
                          <button data-testid={`flow-remove-node-${n.id}`} onClick={() => removeNode(n.id)} className="text-white/40 hover:text-red-400"><Trash2 size={14} /></button>
                        )}
                      </div>
                    </div>

                    {n.type === 'trigger' && (
                      <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr] gap-2">
                        <select data-testid={`flow-trigger-type-${n.id}`} value={n.data.triggerType || 'DM_KEYWORD'} onChange={e => updateNode(n.id, { triggerType: e.target.value })}
                          className="p-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm">
                          {TRIGGER_TYPES.map(t => <option key={t.v} value={t.v} className="bg-[#1a1a1a]">{t.label}</option>)}
                        </select>
                        <input data-testid={`flow-trigger-keyword-${n.id}`} value={n.data.keyword || ''} onChange={e => updateNode(n.id, { keyword: e.target.value.toUpperCase() })}
                          placeholder="Keyword (e.g. GUIDE)" className="p-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm" />
                      </div>
                    )}
                    {n.type === 'sendMessage' && (
                      <textarea data-testid={`flow-message-${n.id}`} rows={2} value={n.data.message || ''} onChange={e => updateNode(n.id, { message: e.target.value })}
                        placeholder="Message text — use {{first_name}} for personalisation" className="w-full p-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm resize-none" />
                    )}
                    {n.type === 'delay' && (
                      <div className="flex items-center gap-2">
                        <input data-testid={`flow-delay-${n.id}`} type="number" min="1" value={n.data.duration || 5} onChange={e => updateNode(n.id, { duration: Number(e.target.value) })}
                          className="w-24 p-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm" />
                        <span className="text-white/50 text-sm">seconds</span>
                      </div>
                    )}
                    {n.type === 'tagUser' && (
                      <input data-testid={`flow-tag-${n.id}`} value={n.data.tagName || ''} onChange={e => updateNode(n.id, { tagName: e.target.value })}
                        placeholder="Tag name" className="w-full p-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm" />
                    )}
                    {n.type === 'sendButtons' && (
                      <div className="space-y-2">
                        <textarea rows={2} value={n.data.message || ''} onChange={e => updateNode(n.id, { message: e.target.value })}
                          placeholder="Message text" className="w-full p-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm resize-none" />
                        <div className="space-y-1.5">
                          {(n.data.buttons || []).map((b, bi) => (
                            <div key={bi} className="grid grid-cols-[1fr_2fr_auto] gap-1.5">
                              <input value={b.title || ''} onChange={e => updateButton(n.id, bi, { title: e.target.value })}
                                placeholder="Button label" className="p-2 rounded-lg bg-white/5 border border-white/10 text-white text-xs" />
                              <input value={b.url || ''} onChange={e => updateButton(n.id, bi, { url: e.target.value })}
                                placeholder="https://" className="p-2 rounded-lg bg-white/5 border border-white/10 text-white text-xs" />
                              <button onClick={() => removeButton(n.id, bi)} className="px-2 rounded-lg bg-white/5 hover:bg-red-500/20 text-white/50 hover:text-red-400"><Trash2 size={12} /></button>
                            </div>
                          ))}
                          <button onClick={() => addButton(n.id)} className="text-xs text-emerald-400 hover:text-emerald-300 flex items-center gap-1"><Plus size={12} /> Add button</button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Add node palette */}
              <div className="p-4 rounded-xl border border-dashed border-white/15">
                <p className="text-white/40 text-xs mb-2">Add a step</p>
                <div className="flex flex-wrap gap-2">
                  {['sendMessage', 'sendButtons', 'delay', 'tagUser'].map(t => (
                    <button key={t} data-testid={`flow-add-${t}`} onClick={() => addNode(t)}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs ${NODE_META[t].color} hover:brightness-125`}>
                      {NODE_META[t].icon}{NODE_META[t].label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="p-5 border-t border-white/10 flex items-center justify-between gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                {error ? (
                  <p className="text-red-400 text-xs whitespace-pre-line" data-testid="flow-editor-error">{error}</p>
                ) : (
                  <p className="text-white/40 text-xs">{nodes.length} step{nodes.length === 1 ? '' : 's'}</p>
                )}
              </div>
              <div className="flex gap-2">
                <button onClick={onClose} className="px-4 py-2 rounded-lg bg-white/5 text-white/70 text-sm">Cancel</button>
                <button data-testid="flow-editor-save" onClick={save} disabled={saving}
                  className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium disabled:opacity-50">
                  {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                  Save flow
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
